/** Từ vựng — `S.vocab` của bản mẫu (spec 11): 5 tab trong page head, giữ đủ chức năng đang có. */
import { api } from "./api.ts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Topic, VocabWord, SrsCard } from "@ca/shared";
import { Button, Card, Chip, Fish, Modal, PageHead, Pbar, Seg, Select, Tabs } from "@ca/ui";
import { useRevisionStudy } from "./use-study.ts";
import { useRead, useWrite, Failed, Pages, Voices } from "./revision-ui.tsx";
import type { RevisionProps } from "./revision-theory.tsx";
const WordPage = z.object({
  total: z.number(),
  items: z.array(VocabWord),
  facets: z
    .object({
      pos: z.record(z.string(), z.number()),
      state: z.record(z.string(), z.number()),
      cefr: z.record(z.string(), z.number()),
    })
    .optional(),
});
type Tab = "topic" | "mine" | "srs" | "colloc" | "para";
const TONES = ["blue", "sage", "yellow", "pink"];
const StartNote = ({ children }: { children: string }) => (
  <p className="faint" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>
    ⏺ {children}
  </p>
);
export function Vocab(props: RevisionProps) {
  const { uid } = props;
  const [tab, setTab] = useState<Tab>(
    new URLSearchParams(window.location.search).has("mine") ? "mine" : "topic",
  );
  const topics = useRead(uid, "/v1/vocab/topics", z.array(Topic));
  const mine = useRead(uid, "/v1/me/vocab", z.array(VocabWord));
  const due = useRead(uid, "/v1/me/srs/due", z.array(SrsCard));
  return (
    <>
      <PageHead
        space="gram"
        title="Từ vựng"
        desc="Kho từ của bạn, ôn theo lịch lặp lại ngắt quãng. Lưu từ ngay khi gặp trong bài đọc để nhớ theo ngữ cảnh."
      >
        <Tabs
          label="Từ vựng"
          items={[
            {
              key: "topic",
              label: `Theo chủ đề (${(topics.data?.reduce((n, t) => n + t.count, 0) ?? 0).toLocaleString("vi-VN")})`,
            },
            { key: "mine", label: `Kho của tôi (${mine.data?.length ?? 0})` },
            { key: "srs", label: `Ôn tập hôm nay (${due.data?.length ?? 0})` },
            { key: "colloc", label: "🔗 Collocation" },
            { key: "para", label: "🔁 Paraphrase" },
          ]}
          active={tab}
          onChange={setTab}
        />
      </PageHead>
      {tab === "topic" ? (
        <Topics
          {...props}
          topics={topics.data ?? []}
          error={topics.error}
          pending={topics.isPending}
        />
      ) : tab === "mine" ? (
        <Mine {...props} />
      ) : tab === "srs" ? (
        <Srs
          {...props}
          back={() => {
            setTab("topic");
          }}
        />
      ) : tab === "colloc" ? (
        <Collocations {...props} />
      ) : (
        <Paraphrases {...props} />
      )}
    </>
  );
}
function Topics(props: RevisionProps & { topics: Topic[]; error: Error | null; pending: boolean }) {
  const [topic, setTopic] = useState<Topic | null>(null);
  useEffect(() => {
    const slug = new URLSearchParams(window.location.search).get("topic");
    if (slug) setTopic(props.topics.find((t) => t.slug === slug) ?? null);
  }, [props.topics]);
  if (topic)
    return (
      <TopicWords
        key={topic.id}
        {...props}
        topic={topic}
        back={() => {
          setTopic(null);
        }}
      />
    );
  const total = props.topics.reduce((n, t) => n + t.count, 0);
  return (
    <Card>
      <div className="card-head">
        <div>
          <h2>🗃️ Từ vựng theo chủ đề TOEIC</h2>
          <p className="sub">
            {props.topics.length} chủ đề ETS · kho {total.toLocaleString("vi-VN")} từ đã được Cá
            chia về đúng chủ đề. Bấm một chủ đề để mở danh sách từ và bộ thẻ.
          </p>
        </div>
      </div>
      <Failed error={props.error} />
      {props.pending && <p className="faint">Đang tải kho từ vựng…</p>}
      <div className="grid g4">
        {props.topics.map((t, i) => (
          <button
            key={t.id}
            className="topic"
            style={{ background: `var(--t-${TONES[i % 4] ?? "blue"})` }}
            aria-label={`${t.nameEn ?? t.name} · ${t.name} · ${t.count} từ${t.locked ? " · Pro" : ""}`}
            onClick={() => {
              setTopic(t);
            }}
          >
            <span className="tp-ic">{t.icon ?? "📚"}</span>
            {t.locked && <Chip tone="pro">🔒 Pro</Chip>}
            <b>{t.nameEn ?? t.name}</b>
            <small>{t.name}</small>
            <small>{t.count} từ</small>
          </button>
        ))}
      </div>
      <StartNote>
        Xem danh sách chủ đề chưa tính giờ; bấm vào một chủ đề để mở bộ thẻ thì mới bắt đầu tính.
      </StartNote>
    </Card>
  );
}
const POS: Record<string, string> = {
  all: "Tất cả",
  n: "Danh từ",
  v: "Động từ",
  adj: "Tính từ",
  adv: "Trạng từ",
  other: "Khác",
};
const STATES: Record<string, string> = {
  all: "Tất cả",
  new: "Mới",
  due: "Đến hạn",
  learning: "Đang học",
  remembered: "Đã nhớ",
};
const CEFR: Record<string, string> = {
  all: "Tất cả",
  A1: "A1",
  A2: "A2",
  B1: "B1",
  B2: "B2",
  C: "C1–C2",
  "?": "Chưa xếp",
};
function TopicWords({
  uid,
  notify,
  topic,
  back,
}: RevisionProps & { topic: Topic; back: () => void }) {
  const [search, setSearch] = useState(new URLSearchParams(window.location.search).get("q") ?? "");
  const [pos, setPos] = useState("all");
  const [state, setState] = useState("all");
  const [cefr, setCefr] = useState("all");
  const [flash, setFlash] = useState<number | null>(null);
  const [flip, setFlip] = useState(false);
  const query = useInfiniteQuery({
    queryKey: ["revision", uid, "topic-words", topic.slug, search, pos, state, cefr],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      WordPage.parse(
        await api(
          `/v1/vocab/topics/${topic.slug}/words?limit=30&offset=${pageParam}&q=${encodeURIComponent(search)}&pos=${pos}&state=${state}&cefr=${encodeURIComponent(cefr)}`,
        ),
      ),
    getNextPageParam: (last, pages) => {
      const next = pages.reduce((n, p) => n + p.items.length, 0);
      return next < last.total ? next : undefined;
    },
  });
  const q = {
    ...query,
    data: query.data
      ? {
          total: query.data.pages[0]?.total ?? 0,
          items: query.data.pages.flatMap((p) => p.items),
          facets: query.data.pages[0]?.facets,
        }
      : undefined,
  };
  const write = useWrite(uid, notify);
  async function nextFlash() {
    if ((flash ?? 0) + 1 >= (q.data?.items.length ?? 0) && query.hasNextPage)
      await query.fetchNextPage();
    if ((flash ?? 0) + 1 < (q.data?.total ?? 0)) setFlash((flash ?? 0) + 1);
    else setFlash(null);
    setFlip(false);
  }
  async function gradeFlash(grade: 0 | 1 | 2) {
    const w = flash === null ? null : q.data?.items[flash];
    if (!w) return;
    try {
      const saved = z
        .object({ items: z.array(z.object({ id: z.uuid() })) })
        .parse(
          await write("/v1/me/vocab/bulk", "POST", { words: [{ word: w.word, wordId: w.id }] }),
        );
      const card = SrsCard.nullable().parse(
        await api(`/v1/me/vocab/${saved.items[0]?.id ?? ""}/card`),
      );
      if (card)
        await write(`/v1/me/srs/${card.id}/review`, "POST", {
          grade,
          lastReviewedAt: card.lastReviewedAt,
        });
      await nextFlash();
    } catch {
      /* write helper reports failure */
    }
  }
  useEffect(() => {
    if (flash === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && e.target.closest("input,textarea")) return;
      if (e.key === " ") {
        e.preventDefault();
        setFlip((f) => !f);
      }
      if (e.key === "ArrowRight") void nextFlash();
      if (e.key === "ArrowLeft") {
        setFlash(Math.max(0, flash - 1));
        setFlip(false);
      }
      if (flip && ["1", "2", "3"].includes(e.key))
        void gradeFlash((Number(e.key) - 1) as 0 | 1 | 2);
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [flash, flip, q.data?.items.length]);
  useRevisionStudy(uid, "vocab_topic", q.data?.items.length ? topic.id : undefined, "vocab");
  const filter = (
    label: string,
    names: Record<string, string>,
    value: string,
    set: (x: string) => void,
    facet: "pos" | "state" | "cefr",
  ) => (
    <div className="tag-filter">
      <span className="tf-l">{label}</span>
      {Object.entries(names).map(([x, name]) => (
        <button
          key={x}
          className={`chip${x === value ? " dark" : ""}`}
          aria-pressed={x === value}
          onClick={() => {
            set(x);
          }}
        >
          {name}
          {x !== "all" && <small style={{ opacity: 0.7 }}>{q.data?.facets?.[facet][x] ?? 0}</small>}
        </button>
      ))}
    </div>
  );
  async function save(words: VocabWord[]) {
    try {
      await write("/v1/me/vocab/bulk", "POST", {
        words: words.map((w) => ({ word: w.word, wordId: w.id })),
      });
      notify("Đã thêm từ vào kho và lịch ôn.");
    } catch {
      /* useWrite has already shown the error; keep the current input. */
    }
  }
  const current = flash === null ? null : q.data?.items[flash];
  return (
    <Card className="stack">
      <div className="row">
        <Button variant="line" size="sm" onClick={back}>
          ← Tất cả chủ đề
        </Button>
        <input
          className="input grow"
          style={{ minWidth: 200 }}
          aria-label="Tìm từ trong chủ đề"
          placeholder="Tìm từ hoặc nghĩa trong chủ đề này…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <Button
          size="sm"
          onClick={() => {
            setFlash(0);
            setFlip(false);
          }}
        >
          🎴 Học flashcard ({q.data?.items.length ?? 0} từ)
        </Button>
        <Button variant="soft" size="sm" onClick={() => void save(q.data?.items ?? [])}>
          📚 Thêm {Math.min(30, q.data?.items.length ?? 0)} từ vào ôn
        </Button>
      </div>
      <h2>
        {topic.icon} {topic.nameEn} · {topic.name}{" "}
        <span className="faint" style={{ fontSize: 14 }}>
          — {topic.count} từ · lọc còn {q.data?.total ?? 0}
        </span>
      </h2>
      {filter("Từ loại", POS, pos, setPos, "pos")}
      {filter("Trạng thái", STATES, state, setState, "state")}
      {filter("Bậc CEFR", CEFR, cefr, setCefr, "cefr")}
      <Failed error={q.error} />
      <div className="grid g3">
        {q.data?.items.map((w) => (
          <article key={w.id} className="levelcard">
            <div className="lv-top">
              <b style={{ fontSize: 17 }}>{w.word}</b>
              <Chip tone={!w.learning ? undefined : w.reps >= 3 ? "good" : w.due ? "warn" : "acc"}>
                {!w.learning
                  ? "Từ mới"
                  : w.reps >= 3
                    ? "Đã thuộc"
                    : w.due
                      ? "Đến hạn ôn"
                      : "Đang ôn"}
              </Chip>
            </div>
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
              {w.ipa} · {w.pos} {w.cefr && <Chip>{w.cefr}</Chip>}
            </span>
            <b style={{ fontSize: 14 }}>{w.meaning}</b>
            {w.example && (
              <p style={{ fontStyle: "italic" }}>
                “{w.example}”{" "}
                <span className="faint">
                  ({w.exampleKind === "source" ? "trích từ đề" : "ví dụ mẫu"})
                </span>
              </p>
            )}
            {w.collocation && <p>🔗 {w.collocation}</p>}
            <div className="row between">
              <Voices word={w.word} notify={notify} />
              <Button variant="line" size="sm" onClick={() => void save([w])}>
                {w.learning ? "Ôn lại" : "＋ Ôn"}
              </Button>
            </div>
          </article>
        ))}
      </div>
      <div className="row" style={{ justifyContent: "center" }}>
        {query.hasNextPage && (
          <>
            <Button
              variant="soft"
              size="sm"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Hiện thêm 30 từ
            </Button>
            <Button
              variant="soft"
              size="sm"
              onClick={() =>
                void (async () => {
                  let next = await query.fetchNextPage();
                  while (next.hasNextPage) next = await query.fetchNextPage();
                })().catch((e: unknown) => {
                  notify(e instanceof Error ? e.message : "Chưa tải được.");
                })
              }
            >
              Hiện tất cả
            </Button>
          </>
        )}
        <Button
          variant="line"
          size="sm"
          onClick={() => {
            setPos("all");
            setState("all");
            setCefr("all");
            setSearch("");
          }}
        >
          Xoá lọc
        </Button>
      </div>
      {current && (
        <Modal
          title={`Flashcard · ${(flash ?? 0) + 1}/${q.data?.total ?? 0}`}
          onClose={() => {
            setFlash(null);
          }}
        >
          <div className="stack">
            <Pbar pct={(((flash ?? 0) + 1) / Math.max(1, q.data?.total ?? 1)) * 100} />
            <div
              className="flash"
              role="button"
              tabIndex={0}
              onClick={() => {
                setFlip(!flip);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") setFlip(!flip);
              }}
            >
              <div className="w">{current.word}</div>
              {current.ipa && <div className="ipa">{current.ipa}</div>}
              {flip ? (
                <>
                  <div className="mean">{current.meaning}</div>
                  {current.example && <div className="ex">“{current.example}”</div>}
                </>
              ) : (
                <span className="btn btn-dark">Bấm để lật thẻ</span>
              )}
            </div>
            <div className="row" style={{ justifyContent: "center" }}>
              <Voices word={current.word} notify={notify} />
            </div>
            {flip && (
              <div className="row" style={{ justifyContent: "center" }}>
                {([0, 1, 2] as const).map((grade) => (
                  <Button
                    key={grade}
                    variant={grade === 2 ? "acc" : "soft"}
                    onClick={() => void gradeFlash(grade)}
                  >
                    {["😵 Quên", "🤔 Khó", "😎 Nhớ"][grade]}
                  </Button>
                ))}
              </div>
            )}
            <div className="row between">
              <Button
                variant="soft"
                size="sm"
                disabled={flash === 0}
                onClick={() => {
                  setFlash(Math.max(0, (flash ?? 0) - 1));
                  setFlip(false);
                }}
              >
                ← Trước
              </Button>
              <Button variant="line" size="sm" onClick={() => void save([current])}>
                ＋ Ôn
              </Button>
              <Button variant="soft" size="sm" onClick={() => void nextFlash()}>
                Bỏ qua →
              </Button>
            </div>
            <p className="faint" style={{ fontSize: 12, textAlign: "center", fontWeight: 600 }}>
              Space lật thẻ · 1 Quên · 2 Khó · 3 Nhớ · ← → chuyển từ · Esc thoát
            </p>
          </div>
        </Modal>
      )}
    </Card>
  );
}
function Mine({ uid, notify }: RevisionProps) {
  const q = useRead(uid, "/v1/me/vocab", z.array(VocabWord));
  const [word, setWord] = useState("");
  const [meaning, setMeaning] = useState("");
  const [removing, setRemoving] = useState<VocabWord | null>(null);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("mine");
    if (id) document.getElementById(`word-${id}`)?.scrollIntoView({ block: "center" });
  }, [q.data]);
  const write = useWrite(uid, notify);
  useRevisionStudy(uid, "vocab_library", q.data?.length ? uid : undefined, "vocab");
  async function add() {
    try {
      const found = z
        .object({
          meaning: z.string().nullable(),
          ipa: z.string().nullable().optional(),
          example: z.string().nullable().optional(),
        })
        .parse(await api(`/v1/dictionary/${encodeURIComponent(word)}`));
      await write(`/v1/me/vocab/${crypto.randomUUID()}`, "PUT", {
        word,
        meaning: meaning || found.meaning,
        ipa: found.ipa ?? null,
        example: found.example ?? null,
      });
      setWord("");
      setMeaning("");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Chưa thêm được từ.");
    }
  }
  return (
    <Card className="stack">
      <p className="muted">Từ bạn lưu khi làm bài + từ tự thêm. Bấm ＋ Ôn để đưa vào lịch ôn.</p>
      <div className="row">
        <input
          className="input grow"
          aria-label="Từ mới"
          placeholder="Nhập từ mới (tự tra trong kho)…"
          value={word}
          onChange={(e) => {
            setWord(e.target.value);
          }}
        />
        <input
          className="input grow"
          aria-label="Nghĩa từ mới"
          placeholder="Nghĩa (nếu chưa có trong kho)"
          value={meaning}
          onChange={(e) => {
            setMeaning(e.target.value);
          }}
        />
        <Button variant="acc" disabled={!word.trim()} onClick={() => void add()}>
          ＋ Thêm
        </Button>
      </div>
      <Failed error={q.error} />
      {q.data?.length ? (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Từ</th>
                <th>Nghĩa</th>
                <th>Ví dụ / Collocation</th>
                <th>Trạng thái</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((w) => (
                <tr id={`word-${w.id}`} key={w.id}>
                  <td>
                    <b>{w.word}</b>
                    <div className="faint" style={{ fontSize: 12 }}>
                      {w.ipa}
                    </div>
                    <Voices word={w.word} notify={notify} />
                  </td>
                  <td>{w.meaning}</td>
                  <td className="faint">
                    {w.example}
                    {w.collocation && <div>🔗 {w.collocation}</div>}
                  </td>
                  <td>
                    <Chip tone={w.learning ? (w.due ? "warn" : "acc") : undefined}>
                      {w.learning ? (w.due ? "Đến hạn" : "Đang ôn") : "Chưa ôn"}
                    </Chip>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4, flexWrap: "nowrap" }}>
                      <Button
                        variant="line"
                        size="sm"
                        onClick={() =>
                          void write(`/v1/me/vocab/${w.id}`, "PUT", {
                            word: w.word,
                            review: true,
                          }).catch(() => {})
                        }
                      >
                        ＋ Ôn
                      </Button>
                      <Button
                        variant="soft"
                        size="sm"
                        aria-label="Xoá"
                        onClick={() => {
                          setRemoving(w);
                        }}
                      >
                        🗑
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !q.isPending && (
          <p className="faint">
            Bấm vào từ gạch chấm trong bài đọc để lưu, hoặc nhập một từ ở trên.
          </p>
        )
      )}
      <StartNote>Thêm từ, xem bảng chưa tính giờ; chỉ tính khi bạn ôn bằng thẻ.</StartNote>
      {removing && (
        <Modal
          title="Xoá từ"
          className="sm"
          onClose={() => {
            setRemoving(null);
          }}
        >
          <p className="muted">Xoá “{removing.word}” khỏi kho và lịch ôn?</p>
          <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setRemoving(null);
              }}
            >
              Huỷ
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const w = removing;
                setRemoving(null);
                void write(`/v1/me/vocab/${w.id}`, "DELETE").catch(() => {});
              }}
            >
              🗑 Xoá
            </Button>
          </div>
        </Modal>
      )}
    </Card>
  );
}
function Srs({ uid, notify, back }: RevisionProps & { back: () => void }) {
  const q = useRead(uid, "/v1/me/srs/due", z.array(SrsCard));
  const [started, setStarted] = useState(false);
  const [flip, setFlip] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doneN, setDoneN] = useState(0);
  const card = started ? q.data?.[0] : undefined;
  const write = useWrite(uid, notify);
  const settle = useRevisionStudy(uid, "srs", card?.id, "vocab");
  async function review(grade: 0 | 1 | 2) {
    if (!card || busy) return;
    setBusy(true);
    try {
      await settle();
      await write(`/v1/me/srs/${card.id}/review`, "POST", {
        grade,
        lastReviewedAt: card.lastReviewedAt,
      });
      setFlip(false);
      setDoneN((n) => n + 1);
    } catch {
      /* The write helper reports failure and the form keeps its current contents. */
    } finally {
      setBusy(false);
    }
  }
  const left = q.data?.length ?? 0;
  return (
    <Card className="stack">
      <Failed error={q.error} />
      {!left && !q.isPending ? (
        <div className="flash">
          <div style={{ width: 110 }}>
            <Fish pose="party" />
          </div>
          <b style={{ fontSize: 18 }}>Hôm nay đã ôn xong</b>
          <p className="muted">Muốn học thêm? Lưu từ khi làm bài, hoặc mở một chủ đề.</p>
          <div className="row">
            <Button size="sm" onClick={back}>
              Mở chủ đề từ vựng
            </Button>
          </div>
        </div>
      ) : !started ? (
        <div className="startbox">
          <div className="row" style={{ gap: 14, flexWrap: "nowrap" }}>
            <div style={{ width: 84, flex: "none" }}>
              <Fish />
            </div>
            <div>
              <b style={{ fontSize: 16 }}>Có {left} thẻ đến hạn hôm nay</b>
              <p className="muted" style={{ fontSize: 13.5 }}>
                Lặp lại ngắt quãng: ôn đúng lúc sắp quên. Giờ học bắt đầu tính khi bạn bấm “Bắt đầu
                ôn”.
              </p>
            </div>
          </div>
          <Button
            size="lg"
            style={{ alignSelf: "flex-start" }}
            onClick={() => {
              setStarted(true);
            }}
          >
            ▶ Bắt đầu ôn
          </Button>
        </div>
      ) : card ? (
        <>
          <div className="row between">
            <b>🎴 Ôn tập hôm nay</b>
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
              Còn {left} thẻ
            </span>
          </div>
          <Pbar pct={(doneN / Math.max(1, doneN + left)) * 100} />
          <div className="flash">
            <div className="w">{card.word.word}</div>
            {card.word.ipa && <div className="ipa">{card.word.ipa}</div>}
            <Voices word={card.word.word} notify={notify} />
            {flip ? (
              <>
                <div className="mean">{card.word.meaning}</div>
                {card.word.example && <div className="ex">“{card.word.example}”</div>}
                {card.word.collocation && <div className="ex">🔗 {card.word.collocation}</div>}
              </>
            ) : (
              <Button
                onClick={() => {
                  setFlip(true);
                }}
              >
                Lật thẻ
              </Button>
            )}
          </div>
          {flip && (
            <div className="row" style={{ justifyContent: "center" }}>
              {([0, 1, 2] as const).map((grade) => (
                <Button
                  key={grade}
                  variant={grade === 2 ? "acc" : "soft"}
                  disabled={busy}
                  aria-label={["Quên", "Khó", "Nhớ"][grade]}
                  onClick={() => void review(grade)}
                >
                  {["😵 Quên", "🤔 Khó", "😎 Nhớ"][grade]}
                </Button>
              ))}
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
}
const Collocation = z.object({
  id: z.uuid(),
  phrase: z.string(),
  meaning: z.string(),
  example: z.string().nullable(),
  test: z.string().nullable(),
});
function Collocations({ uid, notify }: RevisionProps) {
  const [type, setType] = useState("co");
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const sources = useRead(
    uid,
    "/v1/collocations/sources",
    z.array(z.object({ id: z.uuid(), name: z.string() })),
  );
  const q = useRead(
    uid,
    `/v1/collocations?type=${type}&q=${encodeURIComponent(search)}&offset=${offset}&limit=20${source ? `&testId=${source}` : ""}`,
    z.object({ total: z.number(), items: z.array(Collocation) }),
  );
  const write = useWrite(uid, notify);
  useRevisionStudy(uid, "collocation", q.data?.items[0]?.id, "vocab");
  return (
    <Card className="stack">
      <p className="muted" style={{ maxWidth: "62ch" }}>
        Cụm từ đáng học & từ B2+ xuất hiện trong đề, kèm nghĩa và câu ví dụ trích nguyên văn từ bài
        đọc. Ôn ngay sau khi làm đề để tận dụng ngữ cảnh còn nóng.
      </p>
      <div className="row">
        <Select
          label="Nguồn"
          style={{ width: 240 }}
          value={source}
          options={[
            { value: "", label: "Tất cả đề" },
            ...(sources.data ?? []).map((t) => ({ value: t.id, label: t.name })),
          ]}
          onChange={(v) => {
            setSource(v);
            setOffset(0);
          }}
        />
        <Seg
          label="Loại"
          options={[
            { value: "co", label: "Collocation" },
            { value: "vo", label: "Từ vựng B2+" },
          ]}
          value={type}
          onChange={(v) => {
            setType(v);
            setOffset(0);
          }}
        />
        <input
          className="input grow"
          style={{ minWidth: 200 }}
          aria-label="Tìm collocation"
          placeholder="Tìm cụm từ, nghĩa hoặc ví dụ…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
        />
        <Chip>{q.data?.total ?? 0} cụm</Chip>
      </div>
      <Failed error={q.error} />
      <div>
        {q.data?.items.map((c) => (
          <div key={c.id} className="list-row" style={{ alignItems: "flex-start" }}>
            <div style={{ minWidth: 150 }}>
              <b>{c.phrase}</b>
              <div className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
                {c.meaning}
              </div>
            </div>
            <p className="muted grow" style={{ fontSize: 13.5, fontStyle: "italic" }}>
              {c.example ? `“${c.example}”` : ""}
              {c.test && (
                <span className="faint" style={{ fontStyle: "normal" }}>
                  {" "}
                  · {c.test}
                </span>
              )}
            </p>
            <Voices word={c.phrase} notify={notify} />
            <Button
              variant="line"
              size="sm"
              onClick={() =>
                void write(`/v1/me/vocab/${crypto.randomUUID()}`, "PUT", {
                  word: c.phrase,
                  meaning: c.meaning,
                  example: c.example,
                }).catch(() => {})
              }
            >
              ＋ Ôn
            </Button>
          </div>
        ))}
        {q.data && !q.data.items.length && <p className="faint">Không có cụm nào khớp.</p>}
      </div>
      <Pages offset={offset} total={q.data?.total ?? 0} set={setOffset} />
      <StartNote>
        Lướt danh sách chưa tính giờ; bấm ＋ Ôn rồi ôn bằng thẻ thì bắt đầu tính.
      </StartNote>
    </Card>
  );
}
const Para = z.object({
  id: z.uuid(),
  sentence: z.string(),
  hintWords: z.array(z.string()),
  sort: z.number(),
});
function Paraphrases({ uid, notify }: RevisionProps) {
  const [offset, setOffset] = useState(0);
  const [started, setStarted] = useState(false);
  const q = useRead(uid, `/v1/paraphrases?offset=${offset}&limit=20`, z.array(Para));
  if (!started)
    return (
      <Card>
        <div className="startbox">
          <div>
            <b style={{ fontSize: 16 }}>Luyện viết lại câu</b>
            <p className="muted" style={{ fontSize: 13.5 }}>
              Viết lại câu theo cách khác mà giữ nguyên nghĩa, kỹ năng cốt lõi của Part 7. Giờ học
              bắt đầu tính khi bạn bắt đầu viết.
            </p>
          </div>
          <Button
            size="lg"
            style={{ alignSelf: "flex-start" }}
            onClick={() => {
              setStarted(true);
            }}
          >
            ▶ Bắt đầu luyện
          </Button>
        </div>
      </Card>
    );
  return (
    <Card className="stack">
      <Failed error={q.error} />
      {q.data?.map((p) => (
        <ParaCard key={p.id} uid={uid} notify={notify} item={p} />
      ))}
      <Pages
        offset={offset}
        total={offset + (q.data?.length ?? 0) + ((q.data?.length ?? 0) === 20 ? 1 : 0)}
        set={setOffset}
      />
    </Card>
  );
}
function ParaCard({ uid, notify, item }: RevisionProps & { item: z.infer<typeof Para> }) {
  const [answer, setAnswer] = useState("");
  const [model, setModel] = useState("");
  const [focused, setFocused] = useState(false);
  const write = useWrite(uid, notify);
  useRevisionStudy(uid, "paraphrase", focused ? item.id : undefined, "vocab");
  return (
    <div className="stack" style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}>
      <div className="row">
        <Chip>Câu {item.sort + 1}</Chip>
      </div>
      <div className="explain">
        <div className="eyebrow">Câu gốc</div>
        <b style={{ fontSize: 16 }}>{item.sentence}</b>
        {item.hintWords.length > 0 && (
          <div className="row" style={{ gap: 6 }}>
            <span className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
              Từ gợi ý:
            </span>
            {item.hintWords.map((h) => (
              <Chip key={h}>{h}</Chip>
            ))}
          </div>
        )}
      </div>
      <textarea
        className="input"
        rows={3}
        aria-label={`Viết lại câu ${item.sort + 1}`}
        placeholder="Viết lại câu của bạn…"
        value={answer}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onChange={(e) => {
          setAnswer(e.target.value);
        }}
      />
      <div className="row">
        <Button
          variant="acc"
          disabled={!answer.trim()}
          onClick={() =>
            void write(`/v1/paraphrases/${item.id}/reveal`, "POST", { answer })
              .then((raw) => {
                setModel(z.object({ modelAnswer: z.string() }).parse(raw).modelAnswer);
              })
              .catch(() => {})
          }
        >
          So với đáp án mẫu
        </Button>
      </div>
      {model && (
        <div
          className="explain"
          style={{ background: "var(--good-bg)", borderColor: "var(--good-line)" }}
        >
          <b>Đáp án mẫu: {model}</b>
        </div>
      )}
    </div>
  );
}
